import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Card,
  Upload,
  Button,
  Typography,
  Space,
  Alert,
  Divider,
  Input,
  App,
  Form,
} from 'antd';
import { InboxOutlined, SafetyCertificateOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { p12Login, roleHomePath, type LoginResult } from '@/services/session';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';

const { Title, Text } = Typography;
const { Dragger } = Upload;

export default function LoginPage() {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const { message } = App.useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const loginMutation = useMutation<LoginResult, unknown, { file: File; password: string }>({
    mutationFn: ({ file, password }) => p12Login(file, password),
    onSuccess: (data) => {
      message.success(
        t('auth.login.welcome', { name: data.displayName || data.commonName || 'user' }),
      );
      const target = data.redirectTo || roleHomePath(data.role);
      setTimeout(() => {
        window.location.href = target;
      }, 600);
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.Result ||
        error?.message ||
        t('auth.login.error');
      message.error(msg);
    },
  });

  const canSubmit = !!uploadedFile && password.length > 0 && !loginMutation.isPending;

  const handleSubmit = () => {
    if (!uploadedFile || !password) {
      message.warning(t('auth.login.missing') || 'Please select a certificate and enter password');
      return;
    }
    loginMutation.mutate({ file: uploadedFile, password });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        padding: 24,
      }}
    >
      <Card style={{ maxWidth: 560, width: '100%' }}>
        <div style={{ textAlign: 'right', marginBottom: 16 }}>
          <LanguageSwitcher />
        </div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <SafetyCertificateOutlined style={{ fontSize: 56, color: '#1677ff' }} />
          <Title level={3} style={{ marginTop: 12, marginBottom: 4 }}>
            AVIS License Server
          </Title>
          <Text type="secondary">{t('auth.login.subtitle') || 'P12 Certificate Login'}</Text>
        </div>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Dragger
            multiple={false}
            maxCount={1}
            beforeUpload={(file) => {
              setUploadedFile(file);
              return false; // 不自動上傳
            }}
            onRemove={() => {
              setUploadedFile(null);
              return true;
            }}
            accept=".p12,.pfx"
            disabled={loginMutation.isPending}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('auth.login.dropHere') || 'Drop P12 file here or click'}</p>
            <p className="ant-upload-hint">{t('auth.login.dropHint') || '.p12 / .pfx only'}</p>
          </Dragger>

          <Form layout="vertical" onFinish={handleSubmit}>
            <Form.Item label={t('auth.login.password') || 'P12 Password'} required>
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onPressEnter={handleSubmit}
                disabled={loginMutation.isPending}
                placeholder={t('auth.login.passwordPh') || 'Enter P12 password'}
                autoFocus
              />
            </Form.Item>
            <Button
              type="primary"
              size="large"
              block
              htmlType="submit"
              loading={loginMutation.isPending}
              disabled={!canSubmit}
            >
              {t('auth.login.submit') || 'Sign in'}
            </Button>
          </Form>

          {loginMutation.isError && (
            <Alert
              type="error"
              showIcon
              message={t('auth.login.failed') || 'Login failed'}
              description={String(
                (loginMutation.error as any)?.response?.data?.message ??
                  (loginMutation.error as any)?.message ??
                  loginMutation.error,
              )}
            />
          )}
        </Space>

        <Divider style={{ margin: '24px 0 12px' }} />
        <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center' }}>
          {t('auth.login.hint') ||
            'Your P12 is authenticated here; password is never stored on the server.'}
        </Text>
      </Card>
    </div>
  );
}
